"use client";

import * as React from "react";
import { RootState } from "@/store/store";
import { useDispatch, useSelector } from "react-redux";
import { User } from "@/components/Types";
import { LANGUAGES, UI_LOCALE_LANGUAGES } from "@/lib/constants";
import { createContext, useContext, useState, useEffect } from "react";
import { UiStrings } from "@/translations/UiStrings";
import { UiStringsEn } from "@/translations/UiStringsEn";
import { getCookie, setCookie } from "cookies-next";
import { isBlank } from "@/lib/utils";
import { setLocale } from "@/store/reducers/appSlice";
import { enGB, ar, es, ru, Locale } from "date-fns/locale";
import { StoryUniverse } from "@/lib/types/story";
import { StoryUiStrings } from "@/translations-story/StoryUiStrings";

const localeMap = {
  spanish: "StoryUiStringsSpanish",
  english: "StoryUiStringsEnglish",
};

// Create context for locale
type StoryLocaleContextType = {
  language: string;
  ts: StoryUiStrings | null; // typeof UiStringsEn (for development)
};

export const StoryLocaleContext = createContext<StoryLocaleContextType>({
  language: 'english',
  ts: null,
});

export interface StoryLocaleProviderProps {
  children: React.ReactNode;  
}

const allowedLocales = Object.keys(UI_LOCALE_LANGUAGES);

export function StoryLocaleProvider({ children }: StoryLocaleProviderProps) {
  const LOGTAG = 'StoryLocaleProvider';

  const universe = useSelector((state: RootState) => state.story.universe) as StoryUniverse | null;    
  const [uiStrings, setUiStrings] = useState<StoryUiStrings | null>(null);
  
  const language = universe?.language || null;

  useEffect(() => {
    // Do not load anything if the language is not set (universe it not loaded)
    if (!language) {
      return;
    }

    // Lazy load the appropriate language strings module
    const loadLanguageStrings = async () => {
      try {
        const moduleName = localeMap[language as keyof typeof localeMap];        
        const module = await import(`@/translations-story/${moduleName}`);
        setUiStrings(module[`${moduleName}`]);
      } catch (error) {
        console.error(`Failed to load story language strings for ${language}`, error);
      }      
    };

    loadLanguageStrings();
  }, [language]);
  
  return (
    <StoryLocaleContext.Provider value={{ language, ts: uiStrings }}>
      {children}
    </StoryLocaleContext.Provider>
  );
}
